import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Stage, Layer, Line, Circle, Image as KonvaImage, Text } from 'react-konva';
import {
  Ruler, AlertCircle, CheckCircle2, Loader2, Sparkles, Download,
  Image as ImageIcon, AlertTriangle, Box, Link2,
  Crosshair, Zap, Save,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { usePlanEditor, wallStyleFor } from './PlanEditorContext';
import ExteriorPreview from '../../maison/ExteriorPreview';
import { PhaseSnapshotsGallery } from './PhaseSnapshotButton';
import MaisonVisualizers from './MaisonVisualizers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const useNativeImage = (url) => {
  const [img, setImg] = useState(null);
  useEffect(() => {
    if (!url) { setImg(null); return undefined; }
    const i = new window.Image();
    i.crossOrigin = 'use-credentials';
    i.onload = () => setImg(i);
    i.onerror = () => setImg(null);
    i.src = url;
    return () => { i.onload = null; i.onerror = null; };
  }, [url]);
  return img;
};

/**
 * TabValidation — V14.50 Sprint 5 (Onglet 4 · Validation & Export 3D).
 *
 * The last stop of the geometry pipeline :
 *   1. Metric CALIBRATION — click 2 points and declare their real-world
 *      distance. Stored as `calibration.scale_m_per_norm_unit`.
 *   2. CONTROLNET MASK — flatten outer polygon + walls + openings into
 *      a strict B&W PNG (`POST /plan-source/render-controlnet-mask`).
 *      Preview is shown inline so the user can iterate.
 *   3. GENERATE 3D — pushes the user to the existing generation flow.
 *      A red badge surfaces unassigned matériauthèque slots so the
 *      architect tops them up before burning GPU credits.
 */
const TabValidation = ({ projectId, selectedLot, selectedLotId, savedState, isMaison = false }) => {
  const isMultiLot = !!selectedLot || !!selectedLotId;
  const { walls, outerPolygon, calibration, setActiveSubTab } = usePlanEditor();
  const [maskUrl, setMaskUrl] = useState(null);
  const [maskMeta, setMaskMeta] = useState(null);
  const [generatingMask, setGeneratingMask] = useState(false);
  const [withRoomTints, setWithRoomTints] = useState(true);
  const [outdoorCutout, setOutdoorCutout] = useState(false);
  const [slotsInfo, setSlotsInfo] = useState(null);
  const [generating3D, setGenerating3D] = useState(false);

  const blockingReason = useMemo(() => {
    if (isMultiLot && !selectedLotId) return 'Sélectionnez un lot dans la barre latérale.';
    if (!savedState?.source_file_id) return 'Uploadez un plan dans l\u2019onglet 1 · Préparation.';
    return null;
  }, [isMultiLot, selectedLotId, savedState]);

  const bgImageUrl = useMemo(() => {
    if (!savedState?.source_file_id) return null;
    const fid = savedState?.cleaned_file_id || savedState.source_file_id;
    const endpoint = savedState?.cleaned_file_id ? 'render' : 'source';
    const lotQ = selectedLotId ? `&lot_id=${selectedLotId}` : '';
    return `${API}/projects/${projectId}/plan-source/${endpoint}?file_id=${fid}${lotQ}`;
  }, [projectId, savedState, selectedLotId]);

  // Force activeSubTab so the StructureCanvas (if shown elsewhere) stays read-only.
  useEffect(() => { setActiveSubTab('validation'); }, [setActiveSubTab]);

  // Poll unassigned slot count
  const fetchSlots = useCallback(() => {
    if (!projectId) return;
    axios.get(`${API}/materialtheque/projects/${projectId}/unassigned-slots-count`,
      { withCredentials: true })
      .then((r) => setSlotsInfo(r.data))
      .catch(() => setSlotsInfo(null));
  }, [projectId]);
  useEffect(() => { fetchSlots(); }, [fetchSlots]);

  const hasGeometry = (walls?.length || 0) > 0
    || (outerPolygon?.length || 0) >= 3
    || (savedState?.structure?.walls?.length || 0) > 0
    || (savedState?.structure?.outer_polygon?.length || 0) >= 3;

  const handleGenerateMask = useCallback(async () => {
    if (blockingReason || !hasGeometry) return;
    setGeneratingMask(true);
    try {
      const tintsQ = withRoomTints ? '&with_room_tints=true' : '';
      const cutoutQ = outdoorCutout ? '&outdoor_cutout=true' : '';
      const url = `${API}/projects/${projectId}/plan-source/render-controlnet-mask${selectedLotId ? `?lot_id=${selectedLotId}` : '?'}${tintsQ}${cutoutQ}`;
      const r = await axios.post(url, null, {
        withCredentials: true,
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(r.data);
      setMaskUrl(blobUrl);
      setMaskMeta({
        id: r.headers['x-mask-id'],
        mode: r.headers['x-mask-mode'],
        walls: parseInt(r.headers['x-mask-wall-count'] || '0', 10),
        openings: parseInt(r.headers['x-mask-opening-count'] || '0', 10),
        rooms: parseInt(r.headers['x-mask-room-count'] || '0', 10),
        withRoomTints: r.headers['x-mask-with-room-tints'] === '1',
        outdoorCutout: r.headers['x-mask-outdoor-cutout'] === '1',
        outdoorRooms: parseInt(r.headers['x-mask-outdoor-rooms'] || '0', 10),
        width: parseInt(r.headers['x-mask-width'] || '0', 10),
        height: parseInt(r.headers['x-mask-height'] || '0', 10),
      });
      const tag = [
        withRoomTints && 'teintes',
        outdoorCutout && 'cutout extérieur',
      ].filter(Boolean).join(' · ');
      toast.success(`Masque ControlNet généré${tag ? ` (${tag})` : ''}`);
    } catch (err) {
      const detail = err?.response?.data?.detail
        || err?.message || 'Génération du masque impossible';
      toast.error(typeof detail === 'string' ? detail : 'Erreur génération masque');
    } finally {
      setGeneratingMask(false);
    }
  }, [projectId, selectedLotId, blockingReason, hasGeometry, withRoomTints, outdoorCutout]);

  // V14.61 Sprint 16 — Replace the « Générer la 3D » button with a
  // semantic « Sauvegarder » that marks the lot's plan structure as
  // explicitly reviewed by the user (in addition to the existing
  // debounced auto-save). The 3D generation is no longer kicked off
  // from here — it belongs to the Generation Studio (or the project
  // dashboard Batch button) where the project-level prompt parameters
  // (typology, style, ambiance…) are actually configured.
  const handleSavePlan = useCallback(async () => {
    // Mono-lot : pas de sélecteur de lot — le backend accepte lot_id absent.
    if (isMultiLot && !selectedLotId) {
      toast.error('Sélectionnez un lot dans la barre latérale.');
      return;
    }
    setGenerating3D(true);
    try {
      const url = `${API}/projects/${projectId}/plan-source/structure/mark-reviewed${selectedLotId ? `?lot_id=${selectedLotId}` : ''}`;
      await axios.post(url, null, { withCredentials: true });
      toast.success('Plan sauvegardé et marqué comme revu');
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Erreur de sauvegarde';
      toast.error(typeof detail === 'string' ? detail : 'Erreur de sauvegarde');
    } finally {
      setGenerating3D(false);
    }
  }, [projectId, selectedLotId, isMultiLot]);

  return (
    <div className="grid grid-cols-12 gap-3 h-full min-h-0" data-testid="tab-validation-root">
      {/* CENTER — calibration canvas + mask preview side-by-side */}
      <div className="col-span-8 min-h-0 flex flex-col rounded-lg border overflow-hidden"
           style={{ background: 'rgba(0,0,0,0.25)', borderColor: 'var(--c-border)' }}>
        <ValidationToolbar
          projectId={projectId}
          selectedLotId={selectedLotId}
          blockingReason={blockingReason}
          hasGeometry={hasGeometry}
          generatingMask={generatingMask}
          onGenerateMask={handleGenerateMask}
          onClearCalibration={null}
          withRoomTints={withRoomTints}
          onToggleRoomTints={setWithRoomTints}
          outdoorCutout={outdoorCutout}
          onToggleOutdoorCutout={setOutdoorCutout}
        />
        <div className="flex-1 min-h-0 grid grid-cols-2 gap-2 p-2">
          <CalibrationPanel bgImageUrl={bgImageUrl} blockingReason={blockingReason} />
          <MaskPreviewPanel maskUrl={maskUrl} maskMeta={maskMeta} generating={generatingMask}
                            hasGeometry={hasGeometry} />
        </div>
      </div>

      {/* RIGHT — Summary + Generate 3D */}
      <div className="col-span-4 min-h-0 flex flex-col rounded-lg border"
           style={{ background: 'rgba(255,255,255,0.02)', borderColor: 'var(--c-border)' }}>
        <ValidationSidebar
          projectId={projectId}
          selectedLotId={selectedLotId}
          slotsInfo={slotsInfo}
          calibration={calibration}
          hasGeometry={hasGeometry}
          onGenerate3D={handleSavePlan}
          onRefreshSlots={fetchSlots}
          blockingReason={blockingReason}
          generating3D={generating3D}
        />
      </div>

      {/* V14.70 — Galerie des images de sauvegarde par phase */}
      <PhaseSnapshotsGallery projectId={projectId} selectedLotId={selectedLotId}
                             savedState={savedState} />

      {/* Sprint 26E/F/G — Visualisation 3D Maison (multi-modes).
          Plein-largeur en bas de la validation pour les projets house_single.
            • SVG composite (legacy Sprint 26E)
            • Shoebox 3D par étage (Sprint 26F)
            • Bâtiment 3D empilé (Sprint 26F)
            • Vue extérieure 3D orbitable (Sprint 26G) */}
      {isMaison && (
        <div className="col-span-12 min-h-0">
          <MaisonVisualizers projectId={projectId} />
        </div>
      )}
    </div>
  );
};

// =====================================================================
// TOOLBAR
// =====================================================================

const ValidationToolbar = ({ blockingReason, hasGeometry, generatingMask, onGenerateMask,
                             withRoomTints, onToggleRoomTints,
                             outdoorCutout, onToggleOutdoorCutout }) => {
  const { calibration } = usePlanEditor();
  return (
    <div className="flex items-center gap-2 px-2.5 py-1.5 border-b flex-wrap"
         style={{ borderColor: 'var(--c-border)', background: 'rgba(255,255,255,0.02)' }}
         data-testid="validation-toolbar">
      <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-300">
        <Crosshair className="w-3.5 h-3.5 text-amber-400" />
        Calibration métrique
      </span>
      {calibration ? (
        <span className="inline-flex items-center gap-1 text-[10.5px] text-teal-300"
              data-testid="calibration-status-set">
          <CheckCircle2 className="w-3 h-3" />
          Échelle : {calibration.scale_m_per_norm_unit?.toFixed(2)} m / unité
          ({calibration.distance_m?.toFixed(2)} m)
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-[10.5px] text-amber-300"
              data-testid="calibration-status-missing">
          <AlertCircle className="w-3 h-3" />
          Non calibré (utilisera des épaisseurs par défaut)
        </span>
      )}
      <div className="w-px h-5 bg-white/10 mx-1" />
      <label className="inline-flex items-center gap-1 text-[10.5px] text-gray-300 cursor-pointer"
             title="Ajoute un calque de teintes sec/humide sous les murs pour orienter ControlNet">
        <input
          type="checkbox"
          checked={!!withRoomTints}
          onChange={(e) => onToggleRoomTints && onToggleRoomTints(e.target.checked)}
          className="w-3 h-3 accent-amber-500"
          data-testid="with-room-tints-toggle"
        />
        Teintes sec/humide
      </label>
      <label className="inline-flex items-center gap-1 text-[10.5px] text-gray-300 cursor-pointer"
             title="Sort les pièces terrasse et jardin du masque (rendues en gris moyen) pour signaler à l'IA qu'il s'agit de volumes extérieurs ouverts">
        <input
          type="checkbox"
          checked={!!outdoorCutout}
          onChange={(e) => onToggleOutdoorCutout && onToggleOutdoorCutout(e.target.checked)}
          className="w-3 h-3 accent-lime-500"
          data-testid="outdoor-cutout-toggle"
        />
        Cutout extérieur (terrasses / jardins)
      </label>
      <Button
        onClick={onGenerateMask}
        disabled={!!blockingReason || !hasGeometry || generatingMask}
        size="sm"
        className="h-7 bg-amber-600 hover:bg-amber-700 text-white text-[11px]"
        data-testid="generate-mask-btn"
      >
        {generatingMask ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                        : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
        {generatingMask ? 'Génération…' : 'Générer le Masque ControlNet'}
      </Button>
    </div>
  );
};

// =====================================================================
// CALIBRATION PANEL — Konva mini-canvas with ruler tool
// =====================================================================

export const CalibrationPanel = ({ bgImageUrl, blockingReason }) => {
  const containerRef = useRef(null);
  const [size, setSize] = useState({ width: 400, height: 400 });
  const [draftP1, setDraftP1] = useState(null);
  const [draftP2, setDraftP2] = useState(null);
  const bgImage = useNativeImage(bgImageUrl || '');
  const { calibration, actions } = usePlanEditor();

  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver((entries) => {
      for (const e of entries) {
        const cr = e.contentRect;
        setSize({ width: Math.max(200, cr.width), height: Math.max(200, cr.height) });
      }
    });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  const transform = useMemo(() => {
    const W = size.width, H = size.height;
    if (!bgImage) return { offsetX: 0, offsetY: 0, scaleX: W, scaleY: H, imgW: W, imgH: H };
    const iw = bgImage.naturalWidth || bgImage.width;
    const ih = bgImage.naturalHeight || bgImage.height;
    if (!iw || !ih) return { offsetX: 0, offsetY: 0, scaleX: W, scaleY: H, imgW: W, imgH: H };
    const scale = Math.min(W / iw, H / ih);
    return {
      offsetX: (W - iw * scale) / 2,
      offsetY: (H - ih * scale) / 2,
      scaleX: iw * scale,
      scaleY: ih * scale,
      imgW: iw * scale,
      imgH: ih * scale,
    };
  }, [bgImage, size]);

  const norm2px = useCallback((nx, ny) => ({
    x: transform.offsetX + nx * transform.scaleX,
    y: transform.offsetY + ny * transform.scaleY,
  }), [transform]);
  const px2norm = useCallback((px, py) => ({
    x: (px - transform.offsetX) / Math.max(1, transform.scaleX),
    y: (py - transform.offsetY) / Math.max(1, transform.scaleY),
  }), [transform]);

  const handleClick = useCallback((e) => {
    const pos = e.target.getStage().getPointerPosition();
    if (!pos) return;
    const n = px2norm(pos.x, pos.y);
    if (!draftP1) setDraftP1([n.x, n.y]);
    else if (!draftP2) {
      const p2 = [n.x, n.y];
      setDraftP2(p2);
      // Prompt for distance in meters
      const val = window.prompt(
        'Distance réelle entre ces 2 points (en mètres) ?',
        '5.00',
      );
      const dist = parseFloat((val || '').replace(',', '.'));
      if (!isNaN(dist) && dist > 0) {
        actions.setCalibrationFromTwoPoints(draftP1, p2, dist);
        setDraftP1(null);
        setDraftP2(null);
      } else {
        // Reset and let user retry
        setDraftP1(null);
        setDraftP2(null);
      }
    }
  }, [draftP1, draftP2, px2norm, actions]);

  const handleReset = useCallback(() => {
    setDraftP1(null);
    setDraftP2(null);
    actions.clearCalibration();
  }, [actions]);

  return (
    <div className="flex flex-col rounded border overflow-hidden h-full"
         style={{ borderColor: 'var(--c-border)' }}
         data-testid="calibration-panel">
      <div className="px-2 py-1 border-b flex items-center gap-1.5 text-[10.5px]"
           style={{ borderColor: 'var(--c-border)', background: 'rgba(0,0,0,0.4)' }}>
        <Ruler className="w-3 h-3 text-amber-400" />
        <span className="text-gray-200 flex-1">
          {draftP1 ? '2/2 · Cliquez le 2nd point' : '1/2 · Cliquez le 1er point connu'}
        </span>
        {calibration && (
          <button type="button" onClick={handleReset}
                  className="text-rose-300 hover:text-rose-200 text-[10px]"
                  data-testid="calibration-reset-btn">
            Réinitialiser
          </button>
        )}
      </div>
      <div ref={containerRef} className="flex-1 relative" style={{ cursor: blockingReason ? 'not-allowed' : 'crosshair' }}>
        {blockingReason ? (
          <div className="absolute inset-0 flex items-center justify-center text-center p-4 text-[11px] text-gray-400">
            <div>
              <AlertCircle className="w-5 h-5 text-amber-400 mx-auto mb-1" />
              {blockingReason}
            </div>
          </div>
        ) : (
          <Stage width={size.width} height={size.height} onClick={handleClick} onTap={handleClick}>
            <Layer listening={false}>
              {bgImage && (
                <KonvaImage image={bgImage}
                            x={transform.offsetX} y={transform.offsetY}
                            width={transform.imgW} height={transform.imgH}
                            opacity={0.7} />
              )}
            </Layer>
            <Layer listening={false}>
              {/* Draft ruler */}
              {draftP1 && (
                <Circle x={norm2px(draftP1[0], draftP1[1]).x}
                        y={norm2px(draftP1[0], draftP1[1]).y}
                        radius={5} fill="#fbbf24" stroke="#000" strokeWidth={1} />
              )}
              {/* Saved calibration ruler */}
              {calibration && calibration.p1 && calibration.p2 && (() => {
                const a = norm2px(calibration.p1[0], calibration.p1[1]);
                const b = norm2px(calibration.p2[0], calibration.p2[1]);
                return (
                  <>
                    <Line points={[a.x, a.y, b.x, b.y]}
                          stroke="#10b981" strokeWidth={3} />
                    <Circle x={a.x} y={a.y} radius={4} fill="#10b981" />
                    <Circle x={b.x} y={b.y} radius={4} fill="#10b981" />
                    <Text x={(a.x + b.x) / 2 + 8} y={(a.y + b.y) / 2 - 8}
                          text={`${calibration.distance_m?.toFixed(2)} m`}
                          fontSize={12} fill="#10b981" stroke="#000" strokeWidth={2}
                          fillAfterStrokeEnabled />
                  </>
                );
              })()}
            </Layer>
          </Stage>
        )}
      </div>
    </div>
  );
};

// =====================================================================
// MASK PREVIEW
// =====================================================================

const MaskPreviewPanel = ({ maskUrl, maskMeta, generating, hasGeometry }) => (
  <div className="flex flex-col rounded border overflow-hidden h-full"
       style={{ borderColor: 'var(--c-border)' }}
       data-testid="mask-preview-panel">
    <div className="px-2 py-1 border-b flex items-center gap-1.5 text-[10.5px]"
         style={{ borderColor: 'var(--c-border)', background: 'rgba(0,0,0,0.4)' }}>
      <ImageIcon className="w-3 h-3 text-amber-400" />
      <span className="text-gray-200 flex-1">Masque ControlNet (B&amp;W)</span>
      {maskMeta && (
        <span className="text-gray-400 text-[10px]">
          {maskMeta.mode} · {maskMeta.walls} murs · {maskMeta.openings} ouvertures
        </span>
      )}
      {maskUrl && (
        <a href={maskUrl} download={`controlnet_mask.png`}
           className="text-emerald-300 hover:text-emerald-200 inline-flex items-center gap-0.5 text-[10px]"
           data-testid="mask-download-btn">
          <Download className="w-3 h-3" /> PNG
        </a>
      )}
    </div>
    <div className="flex-1 relative bg-black flex items-center justify-center">
      {generating ? (
        <div className="text-gray-300 text-[11px] inline-flex items-center gap-1.5">
          <Loader2 className="w-4 h-4 animate-spin" />
          Génération du masque…
        </div>
      ) : maskUrl ? (
        <img src={maskUrl} alt="ControlNet mask" data-testid="mask-preview-img"
             className="max-w-full max-h-full object-contain" />
      ) : !hasGeometry ? (
        <div className="text-center text-[10.5px] text-gray-400 px-4">
          <AlertTriangle className="w-5 h-5 text-amber-400 mx-auto mb-1.5" />
          Aucune géométrie à exporter.<br />
          Dessinez ou détectez des murs dans l&apos;onglet <span className="text-amber-300">2 Structure</span>.
        </div>
      ) : (
        <div className="text-center text-[10.5px] text-gray-500 px-4">
          <Box className="w-5 h-5 mx-auto mb-1.5 opacity-50" />
          Cliquez « Générer le Masque » pour produire le PNG noir &amp; blanc.
        </div>
      )}
    </div>
  </div>
);

// =====================================================================
// VALIDATION SIDEBAR
// =====================================================================

const ValidationSidebar = ({ projectId, selectedLotId, slotsInfo, calibration, hasGeometry,
                             onGenerate3D, onRefreshSlots, blockingReason, generating3D }) => {
  const { walls, openings, rooms } = usePlanEditor();
  const generate3dHref = `/generation?project_id=${projectId}${selectedLotId ? `&lot_id=${selectedLotId}` : ''}`;
  return (
    <div className="flex flex-col h-full min-h-0" data-testid="validation-sidebar">
      <div className="p-2.5 border-b" style={{ borderColor: 'var(--c-border)' }}>
        <h4 className="text-[12px] font-semibold flex items-center gap-1.5"
            style={{ color: 'var(--c-gold)' }}>
          <Zap className="w-3.5 h-3.5" /> Récapitulatif
        </h4>
        <ul className="mt-2 space-y-0.5 text-[10.5px] text-gray-300">
          <li className="flex justify-between">
            <span>Murs</span>
            <span className={walls.length > 0 ? 'text-teal-300' : 'text-gray-500'}>
              {walls.length}
            </span>
          </li>
          <li className="flex justify-between">
            <span>Menuiseries</span>
            <span className={openings.length > 0 ? 'text-teal-300' : 'text-gray-500'}>
              {openings.length}
            </span>
          </li>
          <li className="flex justify-between">
            <span>Pièces</span>
            <span className={rooms.length > 0 ? 'text-teal-300' : 'text-gray-500'}>
              {rooms.length}
            </span>
          </li>
          <li className="flex justify-between">
            <span>Calibration</span>
            <span className={calibration ? 'text-teal-300' : 'text-amber-300'}>
              {calibration ? `${calibration.distance_m?.toFixed(2)} m` : 'Non calibré'}
            </span>
          </li>
        </ul>
      </div>

      <div className="p-2.5 border-b" style={{ borderColor: 'var(--c-border)' }}>
        <div className="flex items-center justify-between">
          <h5 className="text-[11px] font-semibold text-gray-200 inline-flex items-center gap-1">
            <Link2 className="w-3 h-3 text-emerald-400" /> Matériauthèque
          </h5>
          <button type="button" onClick={onRefreshSlots}
                  className="text-[9.5px] text-gray-400 hover:text-gray-200"
                  data-testid="refresh-slots-btn">
            ↻ Rafraîchir
          </button>
        </div>
        {!slotsInfo || !slotsInfo.has_materialtheque ? (
          <p className="text-[10px] text-gray-500 mt-1 italic">
            Aucun slot encore. Posez des menuiseries (onglet 2c) et typez des pièces (onglet 3)
            pour générer automatiquement les sous-rôles requis.
          </p>
        ) : (
          <div className="mt-1.5">
            <div className="flex items-center gap-2 text-[10.5px]">
              <span className="text-teal-300">{slotsInfo.assigned} assignés</span>
              <span className="text-gray-500">/</span>
              <span className="text-amber-300">{slotsInfo.unassigned} à remplir</span>
              <span className="text-gray-500">/</span>
              <span className="text-gray-300">{slotsInfo.total} au total</span>
            </div>
            {slotsInfo.unassigned > 0 && (
              <ul className="mt-1.5 flex flex-wrap gap-1 text-[9.5px]"
                  data-testid="unassigned-subroles-list">
                {Object.entries(slotsInfo.by_subrole || {}).map(([sr, n]) => (
                  <li key={sr}
                      className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-400/30">
                    {sr} <strong>×{n}</strong>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="p-2.5 flex-1 flex flex-col">
        <div className="flex-1" />
        <div className="relative">
          {slotsInfo?.unassigned > 0 && (
            <span
              className="absolute -top-1.5 -right-1.5 bg-rose-500 text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center border-2 border-[rgb(15,15,15)] z-10 pointer-events-none"
              data-testid="generate-3d-badge"
              title={`${slotsInfo.unassigned} slot(s) Matériauthèque à remplir avant rendu optimal`}
            >
              {slotsInfo.unassigned}
            </span>
          )}
          <Button
            onClick={onGenerate3D}
            disabled={!!blockingReason || !hasGeometry || generating3D}
            className="w-full h-10 bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white text-[12px] font-semibold"
            data-testid="save-plan-btn"
            title="Marque le plan comme revu. La génération 3D se lance depuis le Studio de génération avec les paramètres du projet."
          >
            {generating3D ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          : <Save className="w-4 h-4 mr-2" />}
            {generating3D ? 'Sauvegarde…' : 'Sauvegarder'}
          </Button>
        </div>
        {slotsInfo?.unassigned > 0 && (
          <p className="mt-1.5 text-[9.5px] text-amber-300/80 leading-snug text-center">
            <AlertTriangle className="w-3 h-3 inline mr-0.5" />
            {slotsInfo.unassigned} slot(s) Matériauthèque sans matériau —{' '}
            <a href={`/hub/materials/${projectId}`} target="_blank" rel="noopener noreferrer"
               className="underline hover:text-amber-200">
              compléter →
            </a>
          </p>
        )}
      </div>
    </div>
  );
};

export default TabValidation;
